import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import type {
  Company,
  Group,
  GroupWithChildren,
  GroupSelectionState,
  Unit,
  UserPermissions,
} from '@/types';
import {
  buildGroupTree,
  getAllDescendantIds,
  buildHierarchicalGroupStructure,
} from '../FilterContext';

/**
 * Pure derivations over the flat groups list plus the current group selection.
 * Extracted verbatim from FilterProvider (ISS-030, file-length split) — no
 * effects, no async, no cache; behaviour is identical to the inline version.
 */
export function useFilterGroupDerivations(
  groups: Group[],
  companies: Company[],
  selectedGroupIds: string[],
  setSelectedGroupIds: Dispatch<SetStateAction<string[]>>,
) {
  // Build group tree from flat groups list
  const groupTree = useMemo(() => buildGroupTree(groups), [groups]);

  // Pre-compute company → group mapping for O(1) hierarchical search lookups
  const companyGroupsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    groups.forEach(g => {
      if (!map.has(g.company_id)) map.set(g.company_id, new Set());
      map.get(g.company_id)!.add(g.id);
      // Include all descendants for this group
      getAllDescendantIds(g.id, groupTree).forEach(descendantId =>
        map.get(g.company_id)!.add(descendantId)
      );
    });
    return map;
  }, [groups, groupTree]);

  // Pre-compute group → descendants mapping for O(1) lookups
  const groupDescendantsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    groups.forEach(g => {
      map.set(g.id, new Set(getAllDescendantIds(g.id, groupTree)));
    });
    return map;
  }, [groups, groupTree]);

  // Toggle group with cascading selection
  const toggleGroupWithCascade = useCallback((groupId: string) => {
    const descendants = getAllDescendantIds(groupId, groupTree);

    setSelectedGroupIds(prev => {
      if (prev.includes(groupId)) {
        // Deselect parent and all children
        const descendantsSet = new Set(descendants);
        descendantsSet.add(groupId);
        return prev.filter(id => !descendantsSet.has(id));
      } else {
        // Select parent and all children
        const newIds = new Set([...prev, groupId, ...descendants]);
        return Array.from(newIds);
      }
    });
  }, [groupTree, setSelectedGroupIds]);

  // Get selection state for a group (for display)
  const getGroupSelectionState = useCallback((group: GroupWithChildren): GroupSelectionState => {
    const childIds = group.children?.map(c => c.id) || [];
    const selectedChildren = childIds.filter(id => selectedGroupIds.includes(id));
    const isSelected = selectedGroupIds.includes(group.id);

    return {
      groupId: group.id,
      selected: isSelected,
      partiallySelected: !isSelected && selectedChildren.length > 0 && selectedChildren.length < childIds.length,
      childCount: childIds.length,
      selectedChildCount: selectedChildren.length,
    };
  }, [selectedGroupIds]);

  // Build hierarchical groups for sidebar (memoized callback)
  const buildHierarchicalGroups = useCallback((units: Unit[], permissions: UserPermissions) => {
    return buildHierarchicalGroupStructure(units, companies, groups, permissions);
  }, [companies, groups]);

  return {
    groupTree,
    companyGroupsMap,
    groupDescendantsMap,
    toggleGroupWithCascade,
    getGroupSelectionState,
    buildHierarchicalGroups,
  };
}
